import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Đa ngôn ngữ cho bảng quản trị.
 *
 * ── Vì sao từ điển nằm NGAY TRONG file dùng nó ──────────────────────────────
 *
 * Cách thường gặp là gom mọi chuỗi vào một file `en.json` / `vi.json` chung.
 * Ở panel này thì không, vì hai lý do:
 *
 *  1. **Một file chung là một điểm nghẽn.** Panel có gần bốn mươi trang; mỗi
 *     lượt thêm một trang là một lượt sửa cùng một file, và hai người sửa song
 *     song thì đè lên nhau.
 *  2. **Chuỗi rời khỏi chỗ dùng thì hỏng lặng lẽ.** Xoá một nút mà quên xoá
 *     khoá của nó thì không ai biết; đổi câu chữ ở file chung mà không nhìn
 *     thấy ngữ cảnh thì dịch sai nghĩa. Đặt cạnh nhau thì một lượt đọc trang là
 *     thấy đủ cả hai bản.
 *
 * Cái giá phải trả: không có công cụ dịch bên ngoài nào đọc được. Chấp nhận —
 * panel chỉ có hai thứ tiếng và người viết code cũng là người viết bản dịch.
 *
 * ── Kiểu bảo đảm hai bản không lệch nhau ────────────────────────────────────
 *
 * `dict()` suy kiểu từ bản `en` rồi ÉP bản `vi` phải cùng hình dạng. Thiếu một
 * khoá, thừa một khoá, hay một khoá là hàm ở bên này mà là chuỗi ở bên kia —
 * đều là lỗi biên dịch, không phải một ô trống phát hiện ra lúc chạy.
 */

export const PANEL_LANGS = ['en', 'vi'] as const;
export type PanelLang = (typeof PANEL_LANGS)[number];

export const PANEL_LANG_LABEL: Record<PanelLang, string> = {
  en: 'English',
  vi: 'Tiếng Việt',
};

const STORAGE_KEY = 'panel-lang';

export interface Dict<T> {
  readonly en: T;
  readonly vi: T;
}

/**
 * Khai một bảng chuỗi hai thứ tiếng.
 *
 * Giá trị được phép là chuỗi, hoặc hàm trả về chuỗi khi câu có phần thay đổi
 * (`(n) => \`${n} mục\``) — đừng nối chuỗi bằng dấu `+` ở chỗ gọi, vì trật tự
 * các mảnh câu khác nhau giữa hai ngôn ngữ.
 */
export function dict<T extends object>(entries: Dict<T>): Dict<T> {
  return entries;
}

interface LangState {
  readonly lang: PanelLang;
  readonly setLang: (next: PanelLang) => void;
}

const LangContext = createContext<LangState>({ lang: 'en', setLang: () => undefined });

/**
 * Ngôn ngữ mở panel lần đầu.
 *
 * Đọc lựa chọn đã lưu trước; chưa có thì theo ngôn ngữ trình duyệt. Mặc định
 * cuối cùng là tiếng Anh — đó là ngôn ngữ gốc của panel.
 *
 * Đoán theo trình duyệt chứ không ép tiếng Anh cho tất cả: người vận hành hiện
 * tại đang dùng panel tiếng Việt, và bắt họ tự đi tìm nút đổi ngôn ngữ sau một
 * lượt nâng cấp là đổi một thứ đang chạy tốt lấy một màn hình lạ.
 */
function initialLang(): PanelLang {
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved !== null && (PANEL_LANGS as readonly string[]).includes(saved)) {
    return saved as PanelLang;
  }
  return navigator.language.toLowerCase().startsWith('vi') ? 'vi' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [lang, setLangState] = useState<PanelLang>(initialLang);

  useEffect(() => {
    // `lang` của thẻ <html> cho trình đọc màn hình và bộ dịch của trình duyệt
    // biết trang đang ở tiếng gì — thiếu nó thì trình duyệt hỏi "dịch trang này
    // sang tiếng Việt?" ngay trên một trang vốn đã là tiếng Việt.
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo<LangState>(
    () => ({
      lang,
      setLang: (next) => {
        setLangState(next);
        localStorage.setItem(STORAGE_KEY, next);
      },
    }),
    [lang],
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

/** Ngôn ngữ đang chọn — dùng khi cần tự quyết định chứ không tra bảng. */
export function useLang(): LangState {
  return useContext(LangContext);
}

/**
 * Lấy bảng chuỗi theo ngôn ngữ đang chọn.
 *
 * Trả về đúng hình dạng của bản `en`, nên gọi `t.title` hay `t.count(3)` đều
 * được TypeScript kiểm — gõ sai tên khoá là lỗi biên dịch.
 */
export function useT<T extends object>(table: Dict<T>): T {
  const { lang } = useContext(LangContext);
  return table[lang];
}
